import { useEffect } from 'react';
import { Dispatch } from 'redux';
import { Theme } from '@mui/material';
import { MapActions } from '../../../store/Map';
import { MapWellWaterSystemsMetricsActions, ZoomLevel } from '../../../store/wellWaterSystem';
import { updateCountyStyles, updateGeoBlockStyles, updateGeoBlockGroupStyles, updateRegionStyles } from '../../../components/MapSection/features/wellWaterSystem/styles';
import { WellMapRefs } from '../../../types/Map';
import { fetchWellAggregatedWaterSystems } from '../../../components/MapSection/features/wellWaterSystem/services';
import { addMarkers, removeMarkers } from '../../../components/MapSection/features/wellWaterSystem';

interface MapEffectsProps {
  mapRef: React.RefObject<mapboxgl.Map>;
  zoomLevel: string | ZoomLevel;
  regionsHousingUnits: any[];
  countyHousingUnits: any[];
  waterSystemGeoBlocks: any[];
  wellWaterSystemMapMetrics: any;
  wellWaterSystemMapMetricsRef: React.MutableRefObject<any>;
  coordinates: any[];
  selectedState: any;
  refs: WellMapRefs;
  dispatch: Dispatch;
  theme: Theme;
  //setPopupPosition: (position: { x: number; y: number } | null) => void;
  //setPopupDetails: (details: any | null) => void;
  //setFillColor: (color: string) => void;
  prevSelectedCounty: React.MutableRefObject<string | null | undefined>;
  prevSelectedCode: React.MutableRefObject<any>;
  enabled: boolean;
}

export const useWellMapEffects = ({
  mapRef,
  zoomLevel,
  regionsHousingUnits,
  countyHousingUnits,
  waterSystemGeoBlocks,
  wellWaterSystemMapMetrics,
  wellWaterSystemMapMetricsRef,
  coordinates,
  selectedState,
  prevSelectedCode,
  refs,
  dispatch,
  theme,
  prevSelectedCounty,
  enabled,
}: MapEffectsProps) => {

  useEffect(() => {
    if (!enabled) return;
    if (zoomLevel === ZoomLevel.REGION) {
      fetchWellAggregatedWaterSystems(dispatch, zoomLevel as ZoomLevel);
      dispatch(MapWellWaterSystemsMetricsActions.fetchMapWellWaterSystemMetricsAsync({}) as any);
    }
  }, [zoomLevel, dispatch, enabled]);

  // Update region styles
  useEffect(() => {
    if (!enabled) return;
    if (mapRef.current && zoomLevel === ZoomLevel.REGION) {
      if (!mapRef.current.isStyleLoaded()) {
        const onStyleLoaded = () => {
          updateRegionStyles({ mapRef, regionsHousingUnits, regionsHousingUnitsRef: refs.regionsHousingUnitsRef });
          mapRef.current?.off('styledata', onStyleLoaded);
        };
        mapRef.current?.on('styledata', onStyleLoaded);
        return;
      }
      updateRegionStyles({ mapRef, regionsHousingUnits, regionsHousingUnitsRef: refs.regionsHousingUnitsRef });
    }   
  }, [regionsHousingUnits, mapRef, zoomLevel, refs.regionsHousingUnitsRef, enabled]);

  // Update county styles
  useEffect(() => {
    if (!enabled) return;
    if (mapRef.current && countyHousingUnits) {
      updateCountyStyles(mapRef, countyHousingUnits, refs.countyHousingUnitsRef, zoomLevel, prevSelectedCode);
    }
  }, [countyHousingUnits, mapRef, zoomLevel, refs.countyHousingUnitsRef, enabled]);

  // Update geoblock styles
  useEffect(() => {
    if (!enabled) return;
    if (mapRef.current && waterSystemGeoBlocks) {
      updateGeoBlockGroupStyles(mapRef, waterSystemGeoBlocks, refs.geoBlocksGroupUnitsRef, zoomLevel);
    }
  }, [waterSystemGeoBlocks, mapRef, zoomLevel, refs.geoBlocksGroupUnitsRef, enabled]);

  // Handle markers
  useEffect(() => {
    if (!enabled) return;
    if (coordinates?.length) {
      // TODO:
      //addMarkers(mapRef, coordinates, refs.markersRef, theme, setPopupPosition, setPopupDetails, setFillColor, dispatch);
      updateGeoBlockStyles(mapRef, coordinates, refs.geoBlocksUnitsRef, prevSelectedCounty);
    }

    return () => {
      removeMarkers(refs.markersRef);
    };
  }, [coordinates, mapRef, refs.markersRef, theme, dispatch, enabled]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => mapRef.current?.resize();
    window.addEventListener('mapContainerResize', handleResize);
    //window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('mapContainerResize', handleResize);
  }, [mapRef, enabled]);


    //map metrics
    useEffect(() => {
      wellWaterSystemMapMetricsRef.current = wellWaterSystemMapMetrics;
    }, [wellWaterSystemMapMetrics]);

  // Handle selected state(dropdown) changes
  useEffect(() => {
    if (!enabled) return;
    if (selectedState?.location) {
      const [lat, lng] = selectedState.location;
      const zoom = selectedState.default_zoom;
      mapRef.current?.flyTo({
        center: [lng, lat],
        zoom:4,
      });
      mapRef.current?.flyTo({
        center: [lng, lat],
        zoom,
      });

      dispatch(MapActions.setMapCenter({ lat, lng }));
      dispatch(MapActions.setMapZoom({zoom}));
    }
  }, [selectedState, dispatch, mapRef, enabled]);
}; 